import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";

const nodes = [
  { id: "ingest", label: "Data Ingestion", sub: "Upload CT, DICOM, PDF, Video", color: "#FF4B2B", x: 50, y: 30 },
  { id: "identity", label: "Patient Identity (UHID)", sub: "Create / Map / Anonymize", color: "#0D9488", x: 50, y: 130 },
  { id: "storage", label: "Secure Storage", sub: "Encrypted, Organized by Study", color: "#7C3AED", x: 50, y: 230 },
  { id: "viewer", label: "Image Viewer", sub: "Preview, Zoom, Export", color: "#06B6D4", x: 220, y: 130 },
  { id: "reports", label: "Clinical Intelligence", sub: "PDF Analysis & Extraction", color: "#E9730C", x: 220, y: 230 },
  { id: "sharing", label: "Secure Sharing", sub: "Links, Guest Access, Expiry", color: "#EC4899", x: 390, y: 80 },
  { id: "rbac", label: "RBAC & Permissions", sub: "Roles, Granular Access", color: "#EF4444", x: 390, y: 180 },
  { id: "audit", label: "Audit Trail", sub: "Logs, Export, Compliance", color: "#64748B", x: 390, y: 280 },
  { id: "search", label: "Search & Discovery", sub: "Cross-dataset Querying", color: "#0D9488", x: 220, y: 330 },
];

const connections = [
  { from: "ingest", to: "identity" },
  { from: "identity", to: "storage" },
  { from: "identity", to: "viewer" },
  { from: "storage", to: "viewer" },
  { from: "storage", to: "reports" },
  { from: "storage", to: "sharing" },
  { from: "sharing", to: "rbac" },
  { from: "rbac", to: "audit" },
  { from: "storage", to: "search" },
  { from: "reports", to: "search" },
];

const roles = [
  { role: "CT Lab Technicians", actions: ["Upload scans", "View records", "Organize by study"], color: "#E9730C" },
  { role: "Radiologists / Cardiologists", actions: ["Preview images", "Add findings", "Share studies", "Download"], color: "#FF4B2B" },
  { role: "Medical Company / Hospital", actions: ["View shared data", "Download reports", "Access via guest links"], color: "#7C3AED" },
  { role: "Admin", actions: ["Manage users & roles", "Configure anonymization", "View audit trail", "Control data retention"], color: "#EF4444" },
];

function FlowNode({ node }) {
  return (
    <div
      className="flow-node absolute"
      style={{ left: node.x, top: node.y, width: 160 }}
      data-testid={`flow-node-${node.id}`}
    >
      <div className="rounded-lg border border-border bg-card p-3 relative" style={{ borderLeftColor: node.color, borderLeftWidth: 3 }}>
        <div className="w-2.5 h-2.5 rounded-full absolute -left-[7px] top-4" style={{ background: node.color }} />
        <h4 className="text-xs font-semibold">{node.label}</h4>
        <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{node.sub}</p>
      </div>
    </div>
  );
}

export default function Flowchart() {
  const getNodeCenter = (id) => {
    const n = nodes.find(n => n.id === id);
    return { x: n.x + 80, y: n.y + 30 };
  };

  return (
    <div className="space-y-6" data-testid="flowchart-page">
      {/* Platform Flow */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Platform Data Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[420px]">
            <div className="relative" style={{ width: 600, height: 400, minWidth: 600 }}>
              {/* SVG Connections */}
              <svg className="absolute inset-0" width="600" height="400" style={{ zIndex: 0 }}>
                <defs>
                  <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                    <polygon points="0 0, 8 3, 0 6" fill="#8F9DB3" />
                  </marker>
                </defs>
                {connections.map((c, i) => {
                  const from = getNodeCenter(c.from);
                  const to = getNodeCenter(c.to);
                  const midX = (from.x + to.x) / 2;
                  const midY = (from.y + to.y) / 2;
                  return (
                    <line key={i} x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                      stroke="#8F9DB3" strokeWidth="1.5" markerEnd="url(#arrowhead)"
                      strokeDasharray="4 3" opacity="0.6" />
                  );
                })}
              </svg>
              {/* Nodes */}
              {nodes.map(node => <FlowNode key={node.id} node={node} />)}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* User Roles Flow */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">User Roles & Access Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {roles.map((r) => (
              <div key={r.role} className="rounded-lg border border-border bg-secondary/20 p-4" data-testid={`role-flow-${r.role.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full" style={{ background: r.color }} />
                  <h4 className="text-xs font-semibold">{r.role}</h4>
                </div>
                <ul className="space-y-1.5">
                  {r.actions.map((a, i) => (
                    <li key={i} className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: r.color }} />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Data Lifecycle */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Data Lifecycle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {[
              { step: "1", label: "Upload", desc: "CT/DICOM/PDF/Video" },
              { step: "2", label: "Validate", desc: "Format & integrity check" },
              { step: "3", label: "Map to UHID", desc: "Patient identity binding" },
              { step: "4", label: "Store", desc: "Encrypted secure storage" },
              { step: "5", label: "Index", desc: "Metadata extraction" },
              { step: "6", label: "Analyze", desc: "Clinical intelligence" },
              { step: "7", label: "Share", desc: "Controlled distribution" },
              { step: "8", label: "Audit", desc: "Activity logging" },
            ].map((item, i, arr) => (
              <div key={i} className="flex items-center gap-2 flex-shrink-0">
                <div className="w-24 text-center" data-testid={`lifecycle-step-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mx-auto mb-1">
                    {item.step}
                  </div>
                  <p className="text-xs font-medium">{item.label}</p>
                  <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="w-8 h-px bg-border flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
